import { FC, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { NotificationsLevel } from "../../lib/enums";
import { useUrlAnalysisStatus } from "../../modules/network/hooks";
import { hide, show } from "../../notifications/notifications";
import { route } from "../../shim/jsbridge";
import { Link } from "../link";
import useGlobalAnalysisNotificationsContext from "./useGlobalAnalysisNotificationsContext";
import { getAnalysisNotificationStorageKey } from "./utils";
import { prependHttpToUrl } from "../../modules/network/util";

type UrlAnalysisMonitorProps = {
  url: string;
};

const UrlAnalysisMonitor: FC<UrlAnalysisMonitorProps> = ({ url }) => {
  const { url: viewedAnalysisUrl = "" } = useParams();
  const { removeAnalysis } = useGlobalAnalysisNotificationsContext();
  const { isFinished } = useUrlAnalysisStatus(url);
  const hasShownNotificationForThisAnalysis = useRef(false);

  useEffect(() => {
    if (!isFinished || hasShownNotificationForThisAnalysis.current) {
      return;
    }

    const analysisNotificationKey = getAnalysisNotificationStorageKey(url);

    if (localStorage.getItem(analysisNotificationKey)) {
      hasShownNotificationForThisAnalysis.current = true;
      removeAnalysis(url);
      return;
    }

    if (document.visibilityState !== "visible") {
      return;
    }

    hasShownNotificationForThisAnalysis.current = true;
    localStorage.setItem(analysisNotificationKey, Date.now().toString());

    if (viewedAnalysisUrl === url) {
      removeAnalysis(url);
      return;
    }

    const validUrl = prependHttpToUrl(url);

    show({
      id: url,
      level: NotificationsLevel.SUCCESS,
      sticky: true,
      message: (
        <>
          Analysis for {url} has finished. Go to{" "}
          <Link
            variant="notification"
            to={route("network_url_summary", [encodeURIComponent(validUrl)])}
            onClick={() => {
              hide(url);
              removeAnalysis(url);
              localStorage.removeItem(analysisNotificationKey);
            }}
          >
            URL Analysis page
          </Link>
          .
        </>
      ),
      onClose: () => {
        removeAnalysis(url);
        localStorage.removeItem(analysisNotificationKey);
      }
    });
  }, [isFinished, url, removeAnalysis, viewedAnalysisUrl]);

  return null;
};

export default UrlAnalysisMonitor;
